import type { TableColumnsType } from 'antd';
import { StyledTag } from '@app/components/tag/Tag';
import type { StyledTagProps } from '@app/components/tag/Tag';
import type { TokenUsageRow } from '@app/services/api.types';

export type UsageLevel = 'ok' | 'warning' | 'over';

export const computePct = (
    row: Pick<TokenUsageRow, 'total_tokens_used' | 'monthly_token_cap'>
): number =>
    row.monthly_token_cap > 0
        ? Math.round((row.total_tokens_used / row.monthly_token_cap) * 100)
        : 0;

export const getUsageLevel = (pct: number): UsageLevel => {
    if (pct > 100) {
        return 'over';
    }
    return pct >= 80 ? 'warning' : 'ok';
};

const LEVEL_COLOUR: Record<UsageLevel, StyledTagProps['color']> = {
    ok: 'green',
    warning: 'orange',
    over: 'red',
};

const renderPercentTag = (pct: number) => {
    const level = getUsageLevel(pct);
    const label = level === 'over' ? `${pct}% · Over cap` : `${pct}%`;
    return <StyledTag color={LEVEL_COLOUR[level]}>{label}</StyledTag>;
};

export const TOKEN_USAGE_COLUMNS: TableColumnsType<TokenUsageRow> = [
    {
        title: 'Location Name',
        dataIndex: 'name',
        key: 'name',
        className: 'branch-cell',
        width: 260,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (value: string, record) => {
            const count = record.children?.length ?? 0;
            return count > 0 ? (
                <strong>
                    {value}{' '}
                    <span className="text-muted">
                        ({count} location{count === 1 ? '' : 's'})
                    </span>
                </strong>
            ) : (
                value || '-'
            );
        },
    },
    {
        title: 'Brand',
        dataIndex: 'brand_name',
        key: 'brand_name',
        width: 160,
        sorter: (a, b) => (a.brand_name ?? '').localeCompare(b.brand_name ?? ''),
        render: (value: string) => value || '-',
    },
    {
        title: 'Channel Partner',
        dataIndex: 'channel_name',
        key: 'channel_name',
        width: 180,
        sorter: (a, b) => (a.channel_name ?? '').localeCompare(b.channel_name ?? ''),
        render: (value: string) => value || '-',
    },
    {
        title: 'Call Tokens',
        dataIndex: 'call_tokens_used',
        key: 'call_tokens_used',
        width: 150,
        sorter: (a, b) => a.call_tokens_used - b.call_tokens_used,
        render: (value: number) => value.toLocaleString(),
    },
    {
        title: 'WA Tokens',
        dataIndex: 'whatsapp_tokens_used',
        key: 'whatsapp_tokens_used',
        width: 150,
        sorter: (a, b) => a.whatsapp_tokens_used - b.whatsapp_tokens_used,
        render: (value: number) => value.toLocaleString(),
    },
    {
        title: 'Total',
        dataIndex: 'total_tokens_used',
        key: 'total_tokens_used',
        width: 150,
        sorter: (a, b) => a.total_tokens_used - b.total_tokens_used,
        render: (value: number) => value.toLocaleString(),
    },
    {
        title: 'Monthly Cap',
        dataIndex: 'monthly_token_cap',
        key: 'monthly_token_cap',
        width: 150,
        render: (value: number) => value.toLocaleString(),
    },
    {
        title: '% Used',
        key: 'pct_used',
        width: 150,
        sorter: (a, b) => computePct(a) - computePct(b),
        render: (_value, record) => renderPercentTag(computePct(record)),
    },
];
